/**
 * Data transformation utilities for converting between frontend and backend formats
 */

import {
  Workout,
  WorkoutReadDTO,
  WorkoutCreateDTO,
  WorkoutExerciseReadDTO,
  WorkoutExerciseCreateDTO,
  ExerciseBlock,
  WorkoutExerciseSet,
} from '@/types/workout';
import { ExerciseReadDTO } from '@/types/exercise';

/**
 * Convert backend WorkoutReadDTO to frontend Workout format
 */
export function workoutReadDTOToWorkout(dto: WorkoutReadDTO): Workout {
  // Backend returns duration in seconds, convert to minutes
  const durationMinutes = Math.round(dto.duration / 60);
  
  return {
    id: dto.id,
    name: dto.title, // Backend uses 'title', frontend uses 'name'
    description: `A ${durationMinutes}-minute workout`, // Generate description if not available
    duration_minutes: durationMinutes,
    exercises: dto.exercises.map((exerciseDto, index) =>
      workoutExerciseReadDTOToExerciseBlock(exerciseDto, index)
    ),
    created_at: new Date().toISOString(), // Backend doesn't provide this, use current time
    is_preset: false, // Default to false, can be determined by other logic
  };
}

/**
 * Convert backend WorkoutExerciseReadDTO to frontend ExerciseBlock
 */
export function workoutExerciseReadDTOToExerciseBlock(
  dto: WorkoutExerciseReadDTO,
  index: number
): ExerciseBlock {
  // Determine mode from first set
  const firstSet = dto.sets[0];
  const mode: 'time' | 'reps' = firstSet?.duration_seconds ? 'time' : 'reps';

  // Build target object based on mode
  const target: ExerciseBlock['target'] = {
    mode,
  };

  if (mode === 'reps') {
    // For reps mode, use the first set's reps and total sets count
    target.sets = dto.sets.length;
    target.reps = firstSet?.reps;
    target.weight = firstSet?.weight;
    target.rpe = firstSet?.rpe;
  } else {
    // For time mode, use duration from first set
    target.duration_seconds = firstSet?.duration_seconds;
  }

  // Use rest_seconds from first set (assuming all sets have same rest)
  const rest_seconds = firstSet?.rest_seconds || 0;

  return {
    id: `ex-${dto.exercise_id}-${index}`, // Generate ID
    exercise_id: dto.exercise_id,
    name: dto.exercise.name,
    instruction: dto.exercise.description || dto.exercise.name, // Use description or name as instruction
    target,
    rest_seconds,
    superset_group_id: dto.superset_group || undefined,
    order: dto.order,
    phase: dto.phase || 'main', // Phase of workout (warmup/main/cooldown)
    exercise_type: dto.exercise.type, // Exercise's inherent type (separate from phase)
  };
}

/**
 * Convert frontend Workout to backend WorkoutCreateDTO
 */
export function workoutToWorkoutCreateDTO(workout: Workout): WorkoutCreateDTO {
  // Frontend stores duration in minutes, backend expects seconds
  const durationSeconds = workout.duration_minutes * 60;
  
  return {
    title: workout.name, // Frontend uses 'name', backend uses 'title'
    duration: durationSeconds,
    exercises: workout.exercises.map((exercise) =>
      exerciseBlockToWorkoutExerciseCreateDTO(exercise)
    ),
  };
}

/**
 * Convert frontend ExerciseBlock to backend WorkoutExerciseCreateDTO
 */
export function exerciseBlockToWorkoutExerciseCreateDTO(
  block: ExerciseBlock
): WorkoutExerciseCreateDTO {
  const sets: WorkoutExerciseSet[] = [];

  if (block.target.mode === 'reps') {
    // For reps mode, create sets based on target.sets
    const numSets = block.target.sets || 1;
    for (let i = 1; i <= numSets; i++) {
      sets.push({
        set_id: `${block.id}-set-${i}`, // Generate set_id
        set_number: i,
        reps: block.target.reps,
        weight: block.target.weight,
        rest_seconds: block.rest_seconds,
        rpe: block.target.rpe,
      });
    }
  } else {
    // For time mode, create a single set with duration
    sets.push({
      set_id: `${block.id}-set-1`,
      set_number: 1,
      duration_seconds: block.target.duration_seconds,
      rest_seconds: block.rest_seconds,
    });
  }

  return {
    exercise_id: block.exercise_id,
    order: block.order,
    phase: block.phase || 'main', // Phase of workout (warmup/main/cooldown)
    superset_group: block.superset_group_id || null,
    sets,
  };
}

/**
 * Convert WorkoutReadDTO to WorkoutCreateDTO (for updates)
 */
export function workoutReadDTOToWorkoutCreateDTO(
  dto: WorkoutReadDTO
): WorkoutCreateDTO {
  return {
    title: dto.title,
    duration: dto.duration,
    exercises: dto.exercises.map((exercise) => ({
      exercise_id: exercise.exercise_id,
      order: exercise.order,
      phase: exercise.phase || 'main',
      superset_group: exercise.superset_group || null,
      sets: exercise.sets.map((set) => ({
        set_id: set.set_id,
        set_number: set.set_number,
        reps: set.reps,
        duration_seconds: set.duration_seconds,
        weight: set.weight,
        rest_seconds: set.rest_seconds,
        rpe: set.rpe,
        start_time: set.start_time,
        end_time: set.end_time,
      })),
    })),
  };
}

/**
 * Convert ExerciseReadDTO to ExerciseBlock format
 * Used when manually adding exercises to a workout
 */
export function exerciseReadDTOToExerciseBlock(
  exercise: ExerciseReadDTO,
  order: number
): ExerciseBlock {
  // Generate unique ID for the exercise block
  const id = `ex-${exercise.id}-${Date.now()}`;
  
  // Default target values for new exercises
  const target: ExerciseBlock['target'] = {
    mode: 'reps',
    sets: 3,
    reps: 10,
    rest_seconds: 60,
  };
  
  return {
    id,
    exercise_id: exercise.id,
    name: exercise.name,
    instruction: exercise.description || exercise.name,
    target,
    rest_seconds: 60,
    order,
    phase: 'main', // Default to main phase when adding exercises
    exercise_type: exercise.type,
  };
}

